import React from "react";
import _ from "lodash";
import moment from "moment";
import submit from "submit-form";
import { useTranslation } from "react-i18next";
import getDayRangeString from "../../../utils/day-range-string";
import Chart from "../../../parts/chart";
import DateInput from "../../../parts/inputs/date-input";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import ShareModal from "../../../parts/modals/share";
import MetricsFoot from "../../athlete-portal/metrics/MetricsFoot";
import MetricsBackButton from "../../athlete-portal/metrics/metrics-back-button";
import set from "@rill/set";
import { useRillContext } from "../../../rill/RillReact/share/base";

const {
  PERIOD_FORMATS,
  DATE_FORMATS: { DATE_PICKER_FORMAT },
} = require("../../../constants");

function View(props) {
  const { locals } = useRillContext();
  const { t } = useTranslation("dashboard");

  const { exercisesVolumeSplitRawEquipped, query } = locals;
  const { period, units } = query;

  const period_short = PERIOD_FORMATS[period];
  const selectedMetrics =
    query.type === "total"
      ? exercisesVolumeSplitRawEquipped
      : _.pick(exercisesVolumeSplitRawEquipped, [query.type]);
  const keys = _(selectedMetrics)
    .flatMap(_.keys)
    .uniq()
    .map(Number)
    .sortBy()
    .value();
  const labels = _.map(keys, (i) => `${period_short}${i + 1}`);
  const selectedMetricValues = _.values(selectedMetrics);

  const rawSeries = {
    name: "raw",
    className: "raw",
    data: _.map(keys, (key) => {
      return locals.fromMetric(
        _.sumBy(selectedMetricValues, `${key}.raw`),
        units
      );
    }),
  };

  const equippedSeries = {
    name: "equipped",
    className: "equipped",
    data: _.map(keys, (key) => {
      return locals.fromMetric(
        _.sumBy(selectedMetricValues, `${key}.equipped`),
        units
      );
    }),
  };

  const totalSeries = _.zipWith(
    rawSeries.data,
    equippedSeries.data,
    (a = 0, b = 0) => a + b
  );
  const volumeSeries = _.concat(rawSeries, equippedSeries);
  const maxVolume = _.max(totalSeries);

  return (
    <div className="Main Metrics">
      <MetricsBackButton url={`/dashboard/metrics`} />
      <h1 className="raw-equip-vol-split alt">{t("dashboard:rawEquippedVolumeSplit.title")}</h1>
      <span className="description">
        {t("dashboard:rawEquippedVolumeSplit.description")}
      </span>

      <form id="metrics-form" action="#_" method="GET" data-noreset>
        <div className="Date-Range">
          <div className="left-side">
            <span className="label">{t("dashboard:metrics.reportingPeriod")}</span>
            <DateInput
              name="start"
              placeholder={t("dashboard:metrics.startDate")}
              value={query.start}
              max={query.end}
              onChange={handleDayChange}
            />
            <i>{t("dashboard:metrics.to")}</i>
            <DateInput
              name="end"
              placeholder={t("dashboard:metrics.endDate")}
              value={query.end}
              min={query.start}
              onChange={handleDayChange}
            />
            <input type="submit" style={{ opacity: 0, position: "absolute" }} />
          </div>
          <div className="right-side">
            <div className="group">
              <ToggleLinksInput
                name="period"
                options={[
                  { text: t("dashboard:metrics.month"), value: "month" },
                  { text: t("dashboard:metrics.week"), value: "week" },
                  { text: t("dashboard:metrics.day"), value: "day" },
                ]}
                className="border-right"
              />
            </div>
          </div>
        </div>

        <div className="Chart-Frame bar-chart">
          <div className="head">
            <div className="right-side">
              <div className="group">
                <ToggleLinksInput
                  name="units"
                  options={[
                    { text: t("dashboard:rawEquippedVolumeSplit.kg"), value: "metric" },
                    { text: t("dashboard:rawEquippedVolumeSplit.lbs"), value: "imperial" },
                  ]}
                />
              </div>
            </div>

            <div className="left-side">
              <div className="group">
                <ToggleLinksInput
                  className="tabs"
                  name="type"
                  options={[
                    { text: t("dashboard:rawEquippedVolumeSplit.total"), value: "total" },
                    { text: t("dashboard:rawEquippedVolumeSplit.squat"), value: "squat" },
                    { text: t("dashboard:rawEquippedVolumeSplit.bench"), value: "bench" },
                    { text: t("dashboard:rawEquippedVolumeSplit.deadlift"), value: "deadlift" },
                  ]}
                />
              </div>
            </div>
          </div>

          <div className="body">
            {!_.isEmpty(selectedMetrics) && (
              <div>
                <div className="legend">
                  <div className="bar series raw">{t("dashboard:rawEquippedVolumeSplit.rawVolume")}</div>
                  <div className="bar series equipped">{t("dashboard:rawEquippedVolumeSplit.equippedVolume")}</div>
                </div>
                <div className="axis-labels">
                  <div className="left">{t("dashboard:rawEquippedVolumeSplit.volume")}</div>
                </div>
                <div className="chart-fullsize">
                  <div className="labels">
                    <Chart
                      type="Bar"
                      only="label"
                      data={{ series: volumeSeries }}
                      options={{
                        width: "60px",
                        height: "380px",
                        stackBars: true,
                        axisX: { showGrid: false, showLabel: false },
                        axisY: {
                          showGrid: false,
                          showLabel: true,
                          scaleMinSpace: 30,
                          onlyInteger: true,
                          low: 0,
                          high: maxVolume,
                        },
                      }}
                    />
                  </div>

                  <div className="data">
                    <Chart
                      type="Bar"
                      data={{
                        labels: labels,
                        series: volumeSeries,
                      }}
                      options={{
                        width: `${_.size(keys) * 40}px`,
                        height: "380px",
                        chartPadding: { left: 35 },
                        stackBars: true,
                        axisX: { showGrid: false, showLabel: true },
                        axisY: {
                          showGrid: true,
                          showLabel: false,
                          scaleMinSpace: 30,
                          low: 0,
                          high: maxVolume,
                        },
                      }}
                      tooltip={(item, i) => {
                        const key = keys[i];
                        const date = moment
                          .utc(query.start, DATE_PICKER_FORMAT)
                          .add(key, period);
                        const dayRange = getDayRangeString(
                          [
                            date,
                            date.clone().add(1, period).subtract(1, "day"),
                          ],
                          query
                        );
                        const rawTotal = _.round(rawSeries.data[i] || 0, 1);
                        const equippedTotal = _.round(
                          equippedSeries.data[i] || 0,
                          1
                        );
                        return (
                          <div className="tooltip" style={{ left: item.x }}>
                            <div className="details">
                              <div>
                                <b>{dayRange}</b>
                              </div>
                              <div>
                                {t("dashboard:rawEquippedVolumeSplit.raw")}{" "}
                                <b>{locals.withWeight(rawTotal, units)}</b>
                              </div>
                              <div>
                                {t("dashboard:rawEquippedVolumeSplit.equipped")}{" "}
                                <b>{locals.withWeight(equippedTotal, units)}</b>
                              </div>
                            </div>
                          </div>
                        );
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <MetricsFoot
          supportHref="/dashboard/support/videos?video=IQQwEQj8ctw"
          shareHref="/actions/refresh?modal[type]=share"
        />
      </form>
    </div>
  );
}

function handleDayChange() {
  submit("#_", { body: document.getElementById("metrics-form") });
}

const RawEquippedVolumeSplit = (app) => {
  app.get(
    "/raw-equipped-volume-split",
    set("heading", "Raw & Equipped Volume Split"),
    async ({ req, res, load, locals, api, fail }) => {
      const { query } = req;

      _.defaults(req.query, {
        type: "total",
        period: "day",
        units: locals.units,
        start: moment.utc().startOf("month").format(DATE_PICKER_FORMAT),
        end: moment.utc().endOf("month").format(DATE_PICKER_FORMAT),
      });

      await Promise.all([
        load("exercisesVolumeSplitRawEquipped", {
          start: moment
            .utc(query.start, DATE_PICKER_FORMAT)
            .startOf("day")
            .toDate(),
          end: moment.utc(query.end, DATE_PICKER_FORMAT).endOf("day").toDate(),
          period: query.period,
        }),
      ]);

      const modal = _.get(query, "modal", {});

      if (modal.type === "share" && !modal.snapshot) {
        // create share link
        try {
          modal.snapshot = (
            await (
              await api("snapshot", {
                method: "POST",
                body: {
                  type: "raw-equipped-volume-split",
                  data: JSON.stringify(
                    _.chain(locals)
                      .pick(["exercisesVolumeSplitRawEquipped"])
                      .assign({
                        period: query.period,
                        start: query.start,
                        end: query.end,
                      })
                      .value()
                  ),
                },
              })
            ).json()
          )._id;

          modal.date = getDayRangeString([
            moment.utc(query.start, DATE_PICKER_FORMAT),
            moment.utc(query.end, DATE_PICKER_FORMAT),
          ]);
          return res.refreshWith({ modal });
        } catch (e) {
          console.error(e.stack || e);
          fail(400, "Error creating share link.");
        }
      }

      locals.modal = {
        share: <ShareModal />,
      }[modal.type];

      res.body = <View />;
    }
  );
};

export default RawEquippedVolumeSplit;
